import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import type { Branch } from '@/types/index';
import { UnifiedMenuRenderer } from '@/components/UnifiedMenuRenderer';
import { CartProvider } from '@/contexts/CartContext';
import { AlertCircle } from 'lucide-react';
import { useMetaPixel } from '@/hooks/useMetaPixel';
import { MetaPixelSettings } from '@/types/metaPixel';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
// Lazy load PopOfferDialog - only loaded when a pop offer exists
const PopOfferDialog = lazy(() => import('@/components/PopOfferDialog').then(m => ({ default: m.PopOfferDialog })));
import { PoweredByBadge } from '@/components/subscription/PoweredByBadge';
import { PublicMenuSkeleton } from '@/components/loading/PublicMenuSkeleton';
import { usePublicMenuFullData, PublicMenuData } from '@/hooks/usePublicMenuFullData';

export default function PublicMenu() {
  const { slug: branchSlug } = useParams<{ slug: string }>();
  const { i18n, t } = useTranslation();
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [isLanguageReady, setIsLanguageReady] = useState(false);

  // Clean the slug
  const cleanSlug = useMemo(() => branchSlug?.split('&')[0].split('?')[0], [branchSlug]);

  // Single request for ALL menu data
  const { data: fullMenuData, isLoading, error } = usePublicMenuFullData(cleanSlug);
  
  // Extract data from the single response
  const branch = fullMenuData?.branch as Branch | undefined;
  const popOffer = fullMenuData?.popOffer;
  const subscription = fullMenuData?.subscription;

  // Apply branch's default language for public menu - MUST complete before rendering
  useEffect(() => {
    const applyLanguage = async () => {
      if (branch?.default_language) {
        if (i18n.language !== branch.default_language) {
          await i18n.changeLanguage(branch.default_language);
        }
        setIsLanguageReady(true);
      } else if (!isLoading) {
        // No default language set, use current language
        setIsLanguageReady(true);
      }
    };
    applyLanguage();
  }, [branch?.default_language, i18n, isLoading]);

  // Compute ordering enabled from preloaded subscription data
  const effectiveOrderingEnabled = useMemo(() => {
    if (!branch || !subscription) return false;
    const branchAllows = branch.enable_ordering ?? false;
    const subscriptionAllows = subscription.module_online_ordering !== false;
    return branchAllows && subscriptionAllows;
  }, [branch, subscription]);

  // Compute branding tier from preloaded subscription data
  const brandingTier = subscription?.branding_tier || 'none';

  // Meta Pixel setup (only if enabled)
  const pixelSettings: MetaPixelSettings | null = useMemo(() => {
    if (!branch?.meta_pixel_settings) return null;
    return typeof branch.meta_pixel_settings === 'string' 
      ? JSON.parse(branch.meta_pixel_settings) 
      : branch.meta_pixel_settings;
  }, [branch?.meta_pixel_settings]);

  const { trackViewContent } = useMetaPixel({
    pixelId: branch?.meta_pixel_id,
    enabled: branch?.meta_pixel_enabled,
    settings: pixelSettings,
  });

  const title = t('menu.title');

  // Show pop offer dialog when data is loaded
  useEffect(() => {
    if (popOffer && !isLoading) {
      setIsOfferOpen(true);
    }
  }, [popOffer, isLoading]);

  // Loading state - show skeleton until data AND language are ready
  if (isLoading || !isLanguageReady) {
    return <PublicMenuSkeleton />;
  }

  // Error state
  if (error || !branch) {
    return (
      <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} className="bg-gray-50 min-h-screen font-sans">
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white p-4">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Menu Not Found</h1>
          <p className="text-gray-400">{typeof error === 'string' ? error : 'The requested menu could not be found.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} className="bg-gray-50 min-h-screen font-sans">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <CartProvider branch={branch} key={`cart-provider-${i18n.language}`}>
        <UnifiedMenuRenderer
          key={`menu-${i18n.language}`}
          branch={branch}
          enableRatings={branch.enable_ratings || false}
          enableOrdering={effectiveOrderingEnabled}
          onTrackViewContent={trackViewContent}
          preloadedData={fullMenuData}
        />
        
        {/* Powered by Ripplefy badge - use preloaded branding tier */}
        <PoweredByBadge 
          preloadedBrandingTier={brandingTier} 
          variant="light" 
          className="mt-4" 
        />
      </CartProvider>

      {/* Lazy-loaded Pop Offer Dialog - only loads JS when offer exists */}
      {popOffer && (
        <Suspense fallback={null}>
          <PopOfferDialog
            offer={popOffer}
            open={isOfferOpen}
            onOpenChange={setIsOfferOpen}
          />
        </Suspense>
      )}
    </div>
  );
}
